import React from 'react';
import { Table, Tag, Tooltip, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AuthorCell, EntryTableActions, formatLogged } from './entryTable';
import type { ProgramReview } from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface ReviewsTableProps {
	reviews: ProgramReview[];
	loading: boolean;
	/** Whether the signed-in user wrote the review and may revise it. */
	canEdit: (review: ProgramReview) => boolean;
	onView: (review: ProgramReview) => void;
	onViewIssues: (review: ProgramReview) => void;
	onEdit: (review: ProgramReview) => void;
	onDelete: (review: ProgramReview) => void;
}

/** Reviews as one row apiece, for reading the record rather than the prose. */
const ReviewsTable: React.FC<ReviewsTableProps> = ({
	reviews,
	loading,
	canEdit,
	onView,
	onViewIssues,
	onEdit,
	onDelete,
}) => {
	const columns: ColumnsType<ProgramReview> = [
		{
			title: 'Reviewer',
			key: 'reviewer',
			render: (_, review) => (
				<AuthorCell user={review.reviewer} fallback="Unassigned" />
			),
		},
		{
			title: 'Duration',
			dataIndex: 'review_duration_minutes',
			key: 'review_duration_minutes',
			width: 110,
			align: 'right',
			render: (minutes: number | null) =>
				minutes != null ? (
					<Text className="cam-entry__chip">
						<ClockCircleOutlined /> {minutes} min
					</Text>
				) : (
					'—'
				),
		},
		{
			title: 'Issues',
			dataIndex: 'issues_count',
			key: 'issues_count',
			width: 90,
			align: 'right',
			render: (count: number, review) =>
				count > 0 ? (
					<Tooltip title="View these issues">
						<button
							type="button"
							className="cam-tag-button"
							onClick={() => onViewIssues(review)}
						>
							<Tag
								color="warning"
								bordered={false}
								style={{ marginInlineEnd: 0 }}
							>
								{count}
							</Tag>
						</button>
					</Tooltip>
				) : (
					<Text>0</Text>
				),
		},
		{
			title: 'Completed',
			dataIndex: 'review_completed_at',
			key: 'review_completed_at',
			width: 190,
			render: (timestamp: string | null) => (
				<Text>{formatLogged(timestamp)}</Text>
			),
		},
		{
			title: '',
			key: 'actions',
			width: 170,
			align: 'right',
			render: (_, review) => (
				<EntryTableActions
					onView={() => onView(review)}
					onEdit={canEdit(review) ? () => onEdit(review) : undefined}
					onDelete={canEdit(review) ? () => onDelete(review) : undefined}
					deleteConfirmTitle="Remove this review?"
				/>
			),
		},
	];

	return (
		<Table
			className="cam-entry-table"
			size="small"
			rowKey="id"
			loading={loading}
			columns={columns}
			dataSource={reviews}
			pagination={false}
			locale={{ emptyText: 'No reviews yet' }}
		/>
	);
};

export default ReviewsTable;
